/**
 * src/components/pipeline/quote/copilot/copilotChanges.js — TICKET-872. PURE.
 *
 * Turns a copilot turn's `tool_calls` into the change log the panel renders.
 * The shape is the backend's (lib/handlers/quote-copilot/tools.js): every write
 * tool that actually wrote returns a `change: { kind, action, id, label, owner?,
 * review_required? }`, and the loop copies it onto the tool_call record. A call
 * without `change` (a read, a no-match set_line_material, a ToolError) wrote
 * nothing and is ignored.
 *
 * The counting mirrors `summarizeChanges` in lib/handlers/quote-copilot/apply.js.
 * It cannot be imported: that module pulls node:crypto into the browser bundle.
 * Keep the two in step — tests/components/agent/AgentShell.copilot.test.jsx pins the
 * same fixture the backend's apply.test.mjs uses.
 */

/** Render order of the change log. Each key is a `counts` field. */
export const CHANGE_KEYS = Object.freeze([
  'bundles_created', 'bundles_updated',
  'lines_created', 'lines_updated', 'materials_set',
  'open_items_raised', 'open_items_answered',
  'lines_review_required',
]);

/** @param {Array<{change?:object}>} toolCalls */
export function summarizeTurnChanges(toolCalls = []) {
  const counts = Object.fromEntries(CHANGE_KEYS.map((k) => [k, 0]));
  const items = [];
  for (const c of (Array.isArray(toolCalls) ? toolCalls : [])) {
    const ch = c?.change;
    if (!ch) continue;
    items.push(ch);
    if (ch.kind === 'bundle') counts[ch.action === 'created' ? 'bundles_created' : 'bundles_updated'] += 1;
    else if (ch.kind === 'line') {
      counts[ch.action === 'created' ? 'lines_created' : 'lines_updated'] += 1;
      if (ch.review_required) counts.lines_review_required += 1;
    } else if (ch.kind === 'material') counts.materials_set += 1;
    else if (ch.kind === 'open_item') counts[ch.action === 'answered' ? 'open_items_answered' : 'open_items_raised'] += 1;
  }
  return { counts, items, wrote: items.length > 0 };
}

/**
 * "Added 2 lines · set material on 3 lines · raised 1 open question".
 * Keys: quoteCopilot.change.<key>_one / _other with {count}.
 */
export function changeLogText(counts, t) {
  const parts = CHANGE_KEYS
    .filter((k) => counts?.[k] > 0)
    .map((k) => t(`quoteCopilot.change.${k}_${counts[k] === 1 ? 'one' : 'other'}`, { count: counts[k] }));
  const s = parts.join(' · ');
  return s ? s.charAt(0).toUpperCase() + s.slice(1) : '';
}

/** Open items this turn raised, straight from its change records. */
export function raisedOpenItems(toolCalls = []) {
  return summarizeTurnChanges(toolCalls).items
    .filter((ch) => ch.kind === 'open_item' && ch.action === 'raised')
    .map((ch) => ({ id: ch.id, question: ch.label, owner: ch.owner }));
}

/**
 * quote_copilot_turns row -> ChatMessageList message. The assistant row is
 * inserted empty before the loop runs, so an assistant row with no text and no
 * tool calls is a turn still in flight (or one that died): not rendered.
 */
export function turnsToMessages(turns = []) {
  return (Array.isArray(turns) ? turns : [])
    .filter((r) => r.role === 'user' || String(r.content || '').trim() || (r.tool_calls || []).length)
    .map((r) => ({
      id: r.id,
      type: r.role,
      text: r.content || '',
      toolCalls: Array.isArray(r.tool_calls) ? r.tool_calls : [],
      revisionId: r.revision_id || null,
    }));
}
